// Smart Product Popups & Sticky Cart Bar
class SmartPopups {
  constructor() {
    // Initialize settings from theme settings
    this.settings = {
      enableStickyCart: window.Shopify?.theme?.settings?.sticky_cart_enabled !== false,
      stickyCartDelay: (window.Shopify?.theme?.settings?.sticky_cart_delay || 0.5) * 1000,
      stickyCartBackground: window.Shopify?.theme?.settings?.sticky_cart_background || '#ffffff',
      stickyCartTextColor: window.Shopify?.theme?.settings?.sticky_cart_text_color || '#000000',
      stickyCartButtonColor: window.Shopify?.theme?.settings?.sticky_cart_button_color || '#000000',
      stickyCartButtonTextColor: window.Shopify?.theme?.settings?.sticky_cart_button_text_color || '#ffffff'
    };
    
    this.popupCount = 0;
    this.maxPopups = 0; // Disabled
    this.popupInterval = null;
    this.currentProduct = null;
    this.visitedProducts = new Set();
    this.purchasedProducts = new Set();
    this.isProductPage = false;
    
    this.init();
  }
  
  init() {
    this.detectPageType();
    this.loadSessionData();
    this.setupEventListeners();
    this.startPopupSystem();
    this.initStickyCartBar();
  }
  
  detectPageType() {
    // Check if current page is a product page
    this.isProductPage = window.location.pathname.includes('/products/');
    
    if (this.isProductPage) {
      this.currentProduct = this.extractProductInfo();
    } else {
      this.hideStickyCartBar();
    }
  }
  
  extractProductInfo() {
    // Extract product info from the page
    const productData = {
      id: this.getProductId(),
      title: this.getProductTitle(),
      price: this.getProductPrice(),
      image: this.getProductImage(),
      url: window.location.href,
      handle: this.getProductHandle(),
      variants: this.getProductVariants()
    };
    
    return productData;
  }
  
  getProductId() {
    // Try to get product ID from various sources
    const metaTag = document.querySelector('meta[property="product:id"]');
    if (metaTag) return metaTag.content;
    
    const scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (scriptTag) {
      try {
        const data = JSON.parse(scriptTag.textContent);
        return data['@id'] || data.productID;
      } catch (e) {
        console.log('Could not parse product JSON-LD');
      }
    }
    
    return null;
  }
  
  getProductTitle() {
    const titleElement = document.querySelector('h1.product__title, .product-title, h1');
    return titleElement ? titleElement.textContent.trim() : 'Product';
  }
  
  getProductPrice() {
    // Get current price
    const currentPriceElement = document.querySelector('.price__current, .price--current, .price .money, .price');
    const currentPrice = currentPriceElement ? currentPriceElement.textContent.trim() : '$0.00';
    
    // Get compare at price (original price)
    const comparePriceElement = document.querySelector('.price__compare, .price--compare, .price__was, .was-price');
    const comparePrice = comparePriceElement ? comparePriceElement.textContent.trim() : null;
    
    // Return formatted price with discount if available
    if (comparePrice && comparePrice !== currentPrice) {
      return `<span class="current-price">${currentPrice}</span> <span class="was-price">${comparePrice}</span>`;
    }
    
    return currentPrice;
  }
  
  getProductImage() {
    const imageElement = document.querySelector('.product__media img, .product-image img, .product__photo img');
    return imageElement ? imageElement.src : '';
  }
  
  getProductHandle() {
    const path = window.location.pathname;
    return path.split('/products/')[1] || '';
  }
  
  getProductVariants() {
    // Try to get variants from various sources
    const variants = [];
    
    // Method 1: From product JSON-LD
    const scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (scriptTag) {
      try {
        const data = JSON.parse(scriptTag.textContent);
        if (data.offers && Array.isArray(data.offers)) {
          data.offers.forEach(offer => {
            if (offer['@type'] === 'Offer') {
              variants.push({
                id: offer.sku || offer.identifier,
                price: offer.price,
                available: offer.availability === 'https://schema.org/InStock',
                title: offer.name || data.name
              });
            }
          });
        }
      } catch (e) {
        console.log('Could not parse product JSON-LD for variants');
      }
    }
    
    // Method 2: From variant selectors
    const variantSelects = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelects.forEach(select => {
      if (select.tagName === 'SELECT') {
        Array.from(select.options).forEach(option => {
          if (option.value) {
            variants.push({
              id: option.value,
              title: option.textContent,
              available: !option.disabled,
              price: null // Will be filled from price display
            });
          }
        });
      } else if (select.tagName === 'INPUT') {
        variants.push({
          id: select.value,
          title: 'Default',
          available: true,
          price: null
        });
      }
    });
    
    // Method 3: From product form data
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm && variants.length === 0) {
      const formData = new FormData(productForm);
      const variantId = formData.get('id');
      if (variantId) {
        variants.push({
          id: variantId,
          title: 'Default',
          available: true,
          price: null
        });
      }
    }
    
    return variants;
  }
  
  loadSessionData() {
    // Load popup count and visited products from sessionStorage
    const sessionData = sessionStorage.getItem('smartPopupsData');
    if (sessionData) {
      const data = JSON.parse(sessionData);
      this.popupCount = data.popupCount || 0;
      this.visitedProducts = new Set(data.visitedProducts || []);
      this.purchasedProducts = new Set(data.purchasedProducts || []);
    }
  }
  
  saveSessionData() {
    // Save current session data
    const data = {
      popupCount: this.popupCount,
      visitedProducts: Array.from(this.visitedProducts),
      purchasedProducts: Array.from(this.purchasedProducts)
    };
    sessionStorage.setItem('smartPopupsData', JSON.stringify(data));
  }
  
  setupEventListeners() {
    // Track page navigation
    window.addEventListener('beforeunload', () => {
      this.saveSessionData();
    });
    
    // Track cart additions
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-add], .cart-add, .add-to-cart')) {
        this.trackCartAddition();
      }
    });
    
    // Track purchases (checkout button clicks)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-checkout], .checkout, .cart-checkout')) {
        this.trackPurchase();
      }
    });
  }
  
  trackCartAddition() {
    if (this.currentProduct) {
      this.visitedProducts.add(this.currentProduct.id);
      this.saveSessionData();
    }
  }
  
  trackPurchase() {
    if (this.currentProduct) {
      this.purchasedProducts.add(this.currentProduct.id);
      this.saveSessionData();
    }
  }
  
  startPopupSystem() {
    // Disabled - user wants to use app instead
    return;
  }
  
  stopPopupSystem() {
    if (this.popupInterval) {
      clearInterval(this.popupInterval);
      this.popupInterval = null;
    }
  }
  
  async showRandomProductPopup() {
    try {
      const products = await this.getRandomProducts();
      if (products.length > 0) {
        const randomProduct = products[Math.floor(Math.random() * products.length)];
        this.createProductPopup(randomProduct);
        this.popupCount++;
        this.saveSessionData();
      }
    } catch (error) {
      console.error('Error showing product popup:', error);
    }
  }
  
  async getRandomProducts() {
    // Get products with discount tags
    try {
      const response = await fetch('/collections/all/products.json?limit=50');
      const data = await response.json();
      
      // Filter products based on settings
      const discountedProducts = data.products.filter(product => {
        // Check tags from settings
        const tags = this.settings.popupTags.split(',').map(tag => tag.trim().toLowerCase());
        const hasMatchingTag = product.tags.some(tag => 
          tags.some(settingTag => tag.toLowerCase().includes(settingTag))
        );
        
        // Check price range
        const productPrice = product.variants[0]?.price || 0;
        const inPriceRange = productPrice >= this.settings.minPrice && 
                           productPrice <= this.settings.maxPrice;
        
        // Check for sale price
        const hasSalePrice = product.variants.some(variant => 
          variant.compare_at_price && 
          variant.compare_at_price > variant.price
        );
        
        return (hasMatchingTag || hasSalePrice) && inPriceRange;
      });
      
      // Process products to ensure proper image URLs
      const processedProducts = discountedProducts.map(product => {
        return {
          ...product,
          featured_image: product.featured_image || product.images?.[0] || null,
          images: product.images || []
        };
      });
      
      return processedProducts.slice(0, 10); // Return max 10 products
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  }
  
  createProductPopup(product) {
    // Remove existing popup
    const existingPopup = document.querySelector('.smart-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    // Create popup element
    const popup = document.createElement('div');
    popup.className = 'smart-popup';
    
    const variant = product.variants[0];
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    const discountPercent = hasDiscount ? 
      Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
    
    popup.innerHTML = `
      <div class="smart-popup__header">
        <h3 class="smart-popup__title">${this.settings.popupTitle}</h3>
        <button class="smart-popup__close" aria-label="Close popup">×</button>
      </div>
      <div class="smart-popup__content">
        <div class="smart-popup__product">
          <div class="smart-popup__image">
            <img src="${product.featured_image || product.images?.[0] || '/assets/no-image.png'}" alt="${product.title}" loading="lazy" onerror="this.src='/assets/no-image.png'">
          </div>
          <div class="smart-popup__info">
            <h4 class="smart-popup__name">${product.title}</h4>
            <div class="smart-popup__price">
              <span class="smart-popup__current-price">$${(variant.price / 100).toFixed(2)}</span>
              ${hasDiscount ? `<span class="smart-popup__original-price">$${(variant.compare_at_price / 100).toFixed(2)}</span>` : ''}
              ${discountPercent > 0 ? `<span class="smart-popup__discount">-${discountPercent}%</span>` : ''}
            </div>
          </div>
        </div>
        <div class="smart-popup__actions">
          <button class="smart-popup__btn smart-popup__btn--primary" data-product-id="${product.id}" data-variant-id="${variant.id}">
            Add to Cart
          </button>
          <a href="/products/${product.handle}" class="smart-popup__btn smart-popup__btn--secondary">
            View Details
          </a>
        </div>
      </div>
    `;
    
    // Set popup position based on settings
    if (this.settings.popupPosition === 'left') {
      popup.style.right = 'auto';
      popup.style.left = '20px';
    }
    
    // Add to page
    document.body.appendChild(popup);
    
    // Show popup
    setTimeout(() => {
      popup.classList.add('show');
    }, 100);
    
    // Auto hide after configured duration
    setTimeout(() => {
      this.hidePopup(popup);
    }, this.settings.popupDuration);
    
    // Setup event listeners
    this.setupPopupEvents(popup, product, variant);
  }
  
  setupPopupEvents(popup, product, variant) {
    // Close button
    const closeBtn = popup.querySelector('.smart-popup__close');
    closeBtn.addEventListener('click', () => {
      this.hidePopup(popup);
    });
    
    // Add to cart button
    const addToCartBtn = popup.querySelector('.smart-popup__btn--primary');
    addToCartBtn.addEventListener('click', () => {
      this.addToCart(variant.id, 1);
      this.hidePopup(popup);
    });
    
    // Click outside to close
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        this.hidePopup(popup);
      }
    });
  }
  
  hidePopup(popup) {
    popup.classList.add('hide');
    setTimeout(() => {
      if (popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 400);
  }
  
  async addToCart(variantId, quantity) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        this.showNotification('Added to cart!', 'success');
        this.updateCartCount();
        return data;
      } else {
        throw new Error(data.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification(error.message || 'Failed to add to cart', 'error');
      throw error;
    }
  }
  
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCount = cart.item_count;
        
        // Update all possible cart count elements
        const cartSelectors = [
          '.cart-count-bubble',
          '.cart-count',
          '[data-cart-count]',
          '.header__icon--cart .cart-count-bubble',
          '.cart-link .cart-count-bubble',
          '.cart-icon .cart-count-bubble',
          '.header__icon .cart-count-bubble'
        ];
        
        cartSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            element.textContent = cartCount;
            element.style.display = cartCount > 0 ? 'block' : 'none';
          });
        });
        
        // Also update cart drawer if it exists
        const cartDrawer = document.querySelector('.cart-drawer, .drawer--cart');
        if (cartDrawer) {
          const drawerCount = cartDrawer.querySelector('.cart-count-bubble, .cart-count');
          if (drawerCount) {
            drawerCount.textContent = cartCount;
          }
        }
        
        // Trigger cart update event for other scripts
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cart, count: cartCount }
        }));
        
        console.log('Cart count updated to:', cartCount);
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
  }
  
  showCartConfirmationPopup(product, quantity) {
    // Create cart confirmation popup similar to Shopify's default
    const popup = document.createElement('div');
    popup.className = 'cart-confirmation-popup';
    popup.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.3);
      z-index: 10000;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      opacity: 0;
      transition: all 0.3s ease;
    `;
    
    popup.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <div style="color: #4CAF50; margin-right: 12px; font-size: 20px;">✓</div>
        <h3 style="margin: 0; font-size: 18px; color: #333;">Item added to your cart</h3>
        <button class="close-popup" style="margin-left: auto; background: none; border: none; font-size: 20px; cursor: pointer; color: #666;">×</button>
      </div>
      
      <div style="display: flex; align-items: center; margin-bottom: 20px;">
        <img src="${product.image}" alt="${product.title}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 8px; margin-right: 12px;">
        <div>
          <div style="font-weight: 600; color: #333; margin-bottom: 4px;">${product.title}</div>
          <div style="color: #666; font-size: 14px;">Quantity: ${quantity}</div>
        </div>
      </div>
      
      <div style="display: flex; gap: 12px;">
        <button class="view-cart-btn" style="flex: 1; padding: 12px; border: 1px solid #ddd; background: white; border-radius: 8px; cursor: pointer; font-weight: 600;">
          View cart
        </button>
        <button class="checkout-btn" style="flex: 1; padding: 12px; background: #000; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;">
          Check out
        </button>
      </div>
      
      <div style="text-align: center; margin-top: 12px;">
        <a href="#" class="continue-shopping" style="color: #666; text-decoration: underline; font-size: 14px;">Continue shopping</a>
      </div>
    `;
    
    document.body.appendChild(popup);
    
    // Animate in
    setTimeout(() => {
      popup.style.opacity = '1';
    }, 100);
    
    // Add event listeners
    popup.querySelector('.close-popup').addEventListener('click', () => {
      this.hideCartConfirmationPopup(popup);
    });
    
    popup.querySelector('.view-cart-btn').addEventListener('click', () => {
      window.location.href = '/cart';
    });
    
    popup.querySelector('.checkout-btn').addEventListener('click', () => {
      window.location.href = '/checkout';
    });
    
    popup.querySelector('.continue-shopping').addEventListener('click', (e) => {
      e.preventDefault();
      this.hideCartConfirmationPopup(popup);
    });
    
    // Auto close after 5 seconds
    setTimeout(() => {
      this.hideCartConfirmationPopup(popup);
    }, 5000);
  }
  
  hideCartConfirmationPopup(popup) {
    popup.style.opacity = '0';
    setTimeout(() => {
      if (popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 300);
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    
    const colors = {
      success: '#4CAF50',
      error: '#f44336',
      info: '#2196F3'
    };
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type] || colors.info};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 10001;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
  
  // Sticky Cart Bar Methods
  initStickyCartBar() {
    if (this.isProductPage && this.currentProduct && this.settings.enableStickyCart) {
      // Show sticky cart bar after configured delay
      setTimeout(() => {
        this.createStickyCartBar();
      }, this.settings.stickyCartDelay);
    }
  }
  
  createStickyCartBar() {
    // Remove existing sticky cart bar
    const existingBar = document.querySelector('.sticky-cart-bar');
    if (existingBar) {
      existingBar.remove();
    }
    
    const cartBar = document.createElement('div');
    cartBar.className = 'sticky-cart-bar';
    
    cartBar.innerHTML = `
      <div class="sticky-cart-bar__content">
        <div class="sticky-cart-bar__image">
          <img src="${this.currentProduct.image}" alt="${this.currentProduct.title}" loading="lazy">
        </div>
        <div class="sticky-cart-bar__info">
          <h4 class="sticky-cart-bar__name">${this.currentProduct.title}</h4>
          <p class="sticky-cart-bar__price">${this.currentProduct.price}</p>
        </div>
        <div class="sticky-cart-bar__actions">
          <div class="sticky-cart-bar__quantity">
            <button class="sticky-cart-bar__qty-btn" data-action="decrease">-</button>
            <input type="number" class="sticky-cart-bar__qty-input" value="1" min="1" max="99">
            <button class="sticky-cart-bar__qty-btn" data-action="increase">+</button>
          </div>
          <button class="sticky-cart-bar__add-btn" data-product-id="${this.currentProduct.id}">
            Add to Cart
          </button>
          <button class="sticky-cart-bar__close" aria-label="Close cart bar">×</button>
        </div>
      </div>
    `;
    
    // Apply custom colors from settings
    cartBar.style.setProperty('--sticky-cart-bg', this.settings.stickyCartBackground);
    cartBar.style.setProperty('--sticky-cart-text', this.settings.stickyCartTextColor);
    cartBar.style.setProperty('--sticky-cart-btn-bg', this.settings.stickyCartButtonColor);
    cartBar.style.setProperty('--sticky-cart-btn-text', this.settings.stickyCartButtonTextColor);
    
    document.body.appendChild(cartBar);
    
    // Show cart bar
    setTimeout(() => {
      cartBar.classList.add('show');
    }, 500);
    
    // Setup cart bar events
    this.setupCartBarEvents(cartBar);
  }
  
  setupCartBarEvents(cartBar) {
    const qtyInput = cartBar.querySelector('.sticky-cart-bar__qty-input');
    const qtyButtons = cartBar.querySelectorAll('.sticky-cart-bar__qty-btn');
    const addBtn = cartBar.querySelector('.sticky-cart-bar__add-btn');
    const closeBtn = cartBar.querySelector('.sticky-cart-bar__close');
    
    // Quantity buttons
    qtyButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const currentValue = parseInt(qtyInput.value);
        
        if (action === 'increase' && currentValue < 99) {
          qtyInput.value = currentValue + 1;
        } else if (action === 'decrease' && currentValue > 1) {
          qtyInput.value = currentValue - 1;
        }
      });
    });
    
    // Add to cart
    addBtn.addEventListener('click', () => {
      const quantity = parseInt(qtyInput.value);
      this.addToCartFromStickyBar(quantity);
    });
    
    // Close button
    closeBtn.addEventListener('click', () => {
      this.hideStickyCartBar();
    });
  }
  
  async addToCartFromStickyBar(quantity) {
    // Get the first available variant
    const variantId = this.getFirstAvailableVariant();
    
    console.log('Sticky Cart Debug:', {
      variantId: variantId,
      quantity: quantity,
      currentProduct: this.currentProduct,
      variants: this.currentProduct?.variants
    });
    
    if (!variantId) {
      this.showNotification('Product variant not available', 'error');
      console.error('No variant ID found for sticky cart');
      return;
    }
    
    try {
      const result = await this.addToCart(variantId, quantity);
      this.hideStickyCartBar();
      
      // Show cart confirmation popup like the regular add to cart
      this.showCartConfirmationPopup(this.currentProduct, quantity);
      
    } catch (error) {
      console.error('Error adding to cart from sticky bar:', error);
      this.showNotification('Failed to add product to cart', 'error');
    }
  }
  
  getFirstAvailableVariant() {
    // Get the first available variant from the current product
    if (this.currentProduct && this.currentProduct.variants) {
      const availableVariant = this.currentProduct.variants.find(variant => variant.available);
      return availableVariant ? availableVariant.id : this.currentProduct.variants[0]?.id;
    }
    
    // Fallback: try to get variant from page data
    const variantSelect = document.querySelector('select[name="id"], input[name="id"]');
    if (variantSelect) {
      return variantSelect.value;
    }
    
    // Last fallback: get from product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        return variantInput.value;
      }
    }
    
    return null;
  }
  
  showStickyCartBar() {
    const cartBar = document.querySelector('.sticky-cart-bar');
    if (cartBar) {
      cartBar.classList.add('show');
    }
  }
  
  hideStickyCartBar() {
    const cartBar = document.querySelector('.sticky-cart-bar');
    if (cartBar) {
      cartBar.classList.remove('show');
      setTimeout(() => {
        if (cartBar.parentNode) {
          cartBar.parentNode.removeChild(cartBar);
        }
      }, 300);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SmartPopups();
});
